import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  useLoginUserMutation,
  useRegisterUserMutation,
} from "@/features/api/authApi";

const Login = () => {
  const navigate = useNavigate();

  const [signupInput, setSignupInput] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [loginInput, setLoginInput] = useState({ email: "", password: "" });

  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [registerUser, { isLoading: regLoading }] = useRegisterUserMutation();
  const [loginUser, { isLoading: loginLoading }] = useLoginUserMutation();

  const handleInputChange = (e, formType) => {
    const { name, value } = e.target;
    if (formType === "signup") {
      setSignupInput((prev) => ({ ...prev, [name]: value }));
    } else {
      setLoginInput((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateSignupInput = (input) => {
    if (!input.name.trim()) {
      toast.error("Name is required");
      return false;
    }
    if (!input.email.includes("@") || !input.email.includes(".")) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!/^\d{10,15}$/.test(input.phone)) {
      toast.error("Please enter a valid phone number (10–15 digits)");
      return false;
    }
    if (input.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    return true;
  };

  const validateLoginInput = (input) => {
    if (!input.email.includes("@") || !input.email.includes(".")) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!input.password) {
      toast.error("Password is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e, type) => {
    e.preventDefault();
    const input = type === "signup" ? signupInput : loginInput;

    if (type === "signup" && !validateSignupInput(input)) return;
    if (type === "login" && !validateLoginInput(input)) return;

    const action = type === "signup" ? registerUser : loginUser;

    try {
      const response = await action(input).unwrap();
      toast.success(
        response.message ||
          `${type === "signup" ? "Signup" : "Login"} successful`
      );
      navigate("/");
    } catch (err) {
      console.error(`${type} error:`, err);
      toast.error(
        err?.data?.message ||
          `${type === "signup" ? "Signup" : "Login"} failed. Please try again.`
      );
    }
  };

  const renderPasswordInput = (field, value, showPassword, setShowPassword, onChange, idPrefix) => (
    <div className="relative">
      <Input
        id={`${idPrefix}-${field}`}
        name={field}
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={`Enter your ${field}`}
        required
        className="h-11 pr-10"
      />
      <button
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4 pt-16 pb-16">
      <Tabs defaultValue="login" className="w-full max-w-md">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="signup">Signup</TabsTrigger>
          <TabsTrigger value="login">Login</TabsTrigger>
        </TabsList>

        {/* Signup Form */}
        <TabsContent value="signup">
          <Card className="shadow-md">
            <CardHeader>
              <CardTitle>Signup</CardTitle>
              <CardDescription>Create a new account</CardDescription>
            </CardHeader>
            <form onSubmit={(e) => handleSubmit(e, "signup")}>
              <CardContent className="space-y-4">
                {["name", "email", "phone"].map((field) => (
                  <div className="space-y-2" key={field}>
                    <Label htmlFor={`signup-${field}`}>
                      {field[0].toUpperCase() + field.slice(1)}
                    </Label>
                    <Input
                      id={`signup-${field}`}
                      name={field}
                      type={
                        field === "phone"
                          ? "tel"
                          : field === "email"
                          ? "email"
                          : "text"
                      }
                      value={signupInput[field]}
                      onChange={(e) => handleInputChange(e, "signup")}
                      placeholder={`Enter your ${field}`}
                      required
                      disabled={regLoading}
                      className="h-11"
                    />
                  </div>
                ))}

                {/* Password with toggle */}
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  {renderPasswordInput(
                    "password",
                    signupInput.password,
                    showSignupPassword,
                    setShowSignupPassword,
                    (e) => handleInputChange(e, "signup"),
                    "signup"
                  )}
                </div>
              </CardContent>
              <CardFooter className="pt-4">
                <Button type="submit" disabled={regLoading} className="w-full h-11">
                  {regLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Please wait...
                    </>
                  ) : (
                    "Signup"
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>

        {/* Login Form */}
        <TabsContent value="login">
          <Card className="shadow-md">
            <CardHeader>
              <CardTitle>Login</CardTitle>
              <CardDescription>Login to your account</CardDescription>
            </CardHeader>
            <form onSubmit={(e) => handleSubmit(e, "login")}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    name="email"
                    type="email"
                    value={loginInput.email}
                    onChange={(e) => handleInputChange(e, "login")}
                    placeholder="Enter your email"
                    required
                    disabled={loginLoading}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  {renderPasswordInput(
                    "password",
                    loginInput.password,
                    showLoginPassword,
                    setShowLoginPassword,
                    (e) => handleInputChange(e, "login"),
                    "login"
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-3 pt-4">
                <Button type="submit" disabled={loginLoading} className="w-full h-11">
                  {loginLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Please wait...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm text-blue-600 hover:underline"
                  disabled={loginLoading}
                >
                  Forgot Password?
                </button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Login;
